import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'

/**
 * Подписка Supabase Realtime на `orders`, `status_history` и `audit_log`.
 * Любое изменение инвалидирует кэш React Query, чтобы таблица заказов,
 * канбан, тикер и аудит обновлялись без ручного reload.
 *
 * Реализация — module-singleton с отложенным cleanup, переживает React
 * `<StrictMode>` double-mount в dev-режиме. Без этого паттерна два
 * useEffect-mount'а делали два канала, и Supabase Realtime начинал
 * разрывать один из них каждую секунду (CLOSED → SUBSCRIBED → CLOSED…).
 * В этих промежутках INSERT пролетал мимо, и новый заказ в канбане
 * появлялся только после F5 — регресс воспроизводился пользователем.
 *
 * Важные детали, которые нужны вместе, иначе рассыпается:
 *   - Ждём сессию перед subscribe — иначе WebSocket откроется с anon-
 *     токеном, RLS отбросит публикации, канал закроется и пользователь
 *     пропустит события.
 *   - Сразу после получения session явно вызываем `realtime.setAuth()`,
 *     не полагаясь на auto-sync supabase-js.
 *   - На каждую таблицу — свой канал. В supabase-js 2.103 несколько
 *     `postgres_changes`-биндов на одном канале приводят к тому, что
 *     канал уходит в SUBSCRIBED, но сервер не шлёт события (проверено
 *     в Playwright: одиночный bind получает INSERT/UPDATE, а тот же
 *     канал с тремя биндами не получает ничего).
 *   - На SIGNED_IN / SIGNED_OUT пересоздаём каналы, на TOKEN_REFRESHED
 *     вызываем setAuth.
 *
 * Миграция 20260418000000 добавила `status_history` в publication
 * `supabase_realtime` — без неё тикер/аудит не ловили accept_pool_order
 * и change_order_status.
 */

interface RealtimeSingleton {
  cleanup: () => void
}

const SUBSCRIBERS = new Set<QueryClient>()
let singleton: RealtimeSingleton | null = null
let releaseTimer: ReturnType<typeof setTimeout> | null = null

function invalidateAll(
  target:
    | 'orders'
    | 'audit'
    | 'feed-events'
    | 'order'
    | 'order-history'
    | 'billing'
    | 'inspection-leads'
    | 'inspection-lead'
    | 'inspection-lead-history'
    | 'inspection-lead-comments'
    | 'order-comments',
  arg?: string,
) {
  for (const qc of SUBSCRIBERS) {
    if (target === 'orders')
      void qc.invalidateQueries({ queryKey: ['orders'] })
    else if (target === 'audit')
      void qc.invalidateQueries({ queryKey: ['audit'] })
    else if (target === 'feed-events')
      void qc.invalidateQueries({ queryKey: ['feed-events'] })
    else if (target === 'order' && arg)
      void qc.invalidateQueries({ queryKey: ['order', arg] })
    else if (target === 'order-history' && arg)
      void qc.invalidateQueries({ queryKey: ['order-history', arg] })
    else if (target === 'billing' && arg)
      void qc.invalidateQueries({ queryKey: ['billing', arg] })
    else if (target === 'inspection-leads')
      void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
    else if (target === 'inspection-lead' && arg)
      void qc.invalidateQueries({ queryKey: ['inspection-lead', arg] })
    else if (target === 'inspection-lead-history' && arg)
      void qc.invalidateQueries({ queryKey: ['inspection-lead', arg, 'history'] })
    else if (target === 'inspection-lead-comments' && arg)
      void qc.invalidateQueries({ queryKey: ['inspection-lead', arg, 'comments'] })
    else if (target === 'order-comments' && arg)
      void qc.invalidateQueries({ queryKey: ['order-comments', arg] })
  }
}

function log(
  level: 'warn' | 'debug',
  msg: string,
  extra?: Record<string, unknown>,
) {
  if (typeof console === 'undefined') return
  const tag = `[useOrdersRealtime] ${msg}`
  if (level === 'warn') console.warn(tag, extra ?? {})
  else if (console.debug) console.debug(tag, extra ?? {})
}

async function waitForSession(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sb: any,
  timeoutMs = 10_000,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any | null> {
  const start = Date.now()
  let attempt = 0
  while (Date.now() - start < timeoutMs) {
    const { data } = await sb.auth.getSession()
    if (data.session) return data.session
    attempt++
    await new Promise((r) => setTimeout(r, Math.min(100 * attempt, 500)))
  }
  return null
}

function startSingleton(): RealtimeSingleton {
  const sb = createClient()
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let backoffMs = 1_000
  let disposed = false
  // ВАЖНО: supabase-js 2.103 ломается, если навесить несколько
  // `postgres_changes`-биндов на один канал — канал уходит SUBSCRIBED,
  // но сервер не шлёт события (мы потратили полдня, воспроизводя это
  // в Playwright: INSERT приходил только когда на канале был ровно один
  // bind на таблицу). Поэтому держим по одному каналу на таблицу.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channels: any[] = []
  let connectInFlight = false
  let channelsMounted = false

  const scheduleReconnect = () => {
    if (disposed) return
    if (reconnectTimer) clearTimeout(reconnectTimer)
    const delay = backoffMs
    backoffMs = Math.min(backoffMs * 2, 30_000)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void connect()
    }, delay)
  }

  const invalidateOrders = (id?: string | null) => {
    invalidateAll('orders')
    invalidateAll('feed-events')
    invalidateAll('audit')
    if (id) {
      invalidateAll('order', id)
      invalidateAll('billing', id)
    }
  }

  const tearDownChannel = () => {
    while (channels.length) {
      const ch = channels.pop()
      try {
        void sb.removeChannel(ch)
      } catch (e) {
        log('warn', 'removeChannel failed', { error: String(e) })
      }
    }
    channelsMounted = false
  }

  const connect = async () => {
    if (disposed) return
    if (connectInFlight) {
      log('debug', 'connect skipped — already in flight')
      return
    }
    if (channelsMounted) {
      log('debug', 'connect skipped — channels already mounted')
      return
    }
    connectInFlight = true
    try {
      // Ждём сессию — без токена RLS отбросит все публикации.
      const session = await waitForSession(sb)
      if (disposed) return
      if (!session) {
        log('debug', 'no session after timeout → skip connect')
        return
      }

      // Прокидываем пользовательский JWT в Realtime-сокет.
      // supabase-js сам вызывает setAuth при SIGNED_IN, но на холодном
      // клиенте мы можем опередить его auto-sync — делаем явно.
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(sb.realtime as any).setAuth(session.access_token)
      } catch (e) {
        log('warn', 'setAuth failed', { error: String(e) })
      }

      tearDownChannel()
      channelsMounted = true

      const ts = Date.now()
      const handleStatus = (tag: string) => (status: string) => {
        if (status === 'SUBSCRIBED') {
          backoffMs = 1_000
          log('debug', `channel ${tag} SUBSCRIBED`)
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          log('warn', `channel ${tag} fault`, { status })
          // Если хотя бы один канал «умер» — пересобираем все
          // (на практике supabase-js рвёт общий WebSocket, так что
          // остальные тоже недееспособны).
          if (channelsMounted) {
            channelsMounted = false
            scheduleReconnect()
          }
        }
      }

      const subscribeOrdersChannel = () => {
        channels.push(
          sb
            .channel(`admin-orders-${ts}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'orders' },
              (payload: {
                eventType?: string
                new?: { id?: string }
                old?: { id?: string }
              }) => {
                const id = payload.new?.id ?? payload.old?.id
                log('debug', `orders ${payload.eventType ?? '?'}`, { id })
                invalidateOrders(id)
              },
            )
            .subscribe(handleStatus('orders')),
        )
      }
      const subscribeStatusHistoryChannel = () => {
        channels.push(
          sb
            .channel(`admin-status-history-${ts}`)
            .on(
              'postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'status_history' },
              (payload: { new?: { order_id?: string } }) => {
                log('debug', 'status_history INSERT', {
                  orderId: payload.new?.order_id,
                })
                invalidateAll('feed-events')
                invalidateAll('audit')
                const oid = payload.new?.order_id
                if (oid) {
                  invalidateAll('order-history', oid)
                  invalidateAll('billing', oid)
                  invalidateAll('orders')
                }
              },
            )
            .subscribe(handleStatus('status_history')),
        )
      }
      const subscribeAuditLogChannel = () => {
        channels.push(
          sb
            .channel(`admin-audit-log-${ts}`)
            .on(
              'postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'audit_log' },
              () => {
                log('debug', 'audit_log INSERT')
                invalidateAll('audit')
                invalidateAll('feed-events')
              },
            )
            .subscribe(handleStatus('audit_log')),
        )
      }

      const subscribeInspectionLeadsChannel = () => {
        channels.push(
          sb
            .channel(`admin-inspection-leads-${ts}`)
            .on(
              'postgres_changes',
              { event: '*', schema: 'public', table: 'inspection_leads' },
              (payload: {
                eventType?: string
                new?: { id?: string }
                old?: { id?: string }
              }) => {
                const id = payload.new?.id ?? payload.old?.id
                log('debug', `inspection_leads ${payload.eventType ?? '?'}`, { id })
                invalidateAll('inspection-leads')
                if (id) invalidateAll('inspection-lead', id)
              },
            )
            .subscribe(handleStatus('inspection_leads')),
        )
      }
      const subscribeInspectionLeadChangesChannel = () => {
        channels.push(
          sb
            .channel(`admin-inspection-lead-changes-${ts}`)
            .on(
              'postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'inspection_lead_changes' },
              (payload: { new?: { lead_id?: string } }) => {
                const leadId = payload.new?.lead_id
                log('debug', 'inspection_lead_changes INSERT', { leadId })
                if (leadId) invalidateAll('inspection-lead-history', leadId)
              },
            )
            .subscribe(handleStatus('inspection_lead_changes')),
        )
      }

      const subscribeInspectionLeadCommentsChannel = () => {
        channels.push(
          sb
            .channel(`admin-inspection-lead-comments-${ts}`)
            .on(
              'postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'inspection_lead_comments' },
              (payload: { new?: { lead_id?: string } }) => {
                const leadId = payload.new?.lead_id
                log('debug', 'inspection_lead_comments INSERT', { leadId })
                if (leadId) invalidateAll('inspection-lead-comments', leadId)
              },
            )
            .subscribe(handleStatus('inspection_lead_comments')),
        )
      }

      const subscribeOrderCommentsChannel = () => {
        channels.push(
          sb
            .channel(`admin-order-comments-${ts}`)
            .on(
              'postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'order_comments' },
              (payload: { new?: { order_id?: string } }) => {
                const orderId = payload.new?.order_id
                log('debug', 'order_comments INSERT', { orderId })
                if (orderId) invalidateAll('order-comments', orderId)
              },
            )
            .subscribe(handleStatus('order_comments')),
        )
      }

      subscribeOrdersChannel()
      subscribeStatusHistoryChannel()
      subscribeAuditLogChannel()
      subscribeInspectionLeadsChannel()
      subscribeInspectionLeadChangesChannel()
      subscribeInspectionLeadCommentsChannel()
      subscribeOrderCommentsChannel()
    } finally {
      connectInFlight = false
    }
  }

  void connect()

  const { data: authSub } = sb.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN') {
      backoffMs = 1_000
      log('debug', 'SIGNED_IN → connect (if not subscribed)')
      void connect()
    } else if (event === 'SIGNED_OUT') {
      log('debug', 'SIGNED_OUT → teardown')
      tearDownChannel()
    } else if (event === 'TOKEN_REFRESHED') {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(sb.realtime as any).setAuth(session?.access_token ?? null)
        log('debug', 'TOKEN_REFRESHED → setAuth')
      } catch (e) {
        log('warn', 'setAuth failed', { error: String(e) })
      }
    }
  })

  return {
    cleanup: () => {
      disposed = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      authSub?.subscription?.unsubscribe?.()
      tearDownChannel()
    },
  }
}

export function useOrdersRealtime() {
  const qc = useQueryClient()

  useEffect(() => {
    if (USE_MOCK) return

    if (releaseTimer) {
      clearTimeout(releaseTimer)
      releaseTimer = null
    }

    SUBSCRIBERS.add(qc)
    if (!singleton) singleton = startSingleton()

    return () => {
      SUBSCRIBERS.delete(qc)
      if (SUBSCRIBERS.size === 0) {
        releaseTimer = setTimeout(() => {
          releaseTimer = null
          if (SUBSCRIBERS.size === 0 && singleton) {
            singleton.cleanup()
            singleton = null
          }
        }, 200)
      }
    }
  }, [qc])
}
